import url from 'url';
import Vue from 'vue';
import Const from '@/constants';
import ObjectSitesService from '@/api/objectsites';

import TextFormat from '@/helper/textFormatHelper';

/* Hilfsfunktion zum Sortieren von Items anhand der Reihenfolge,
	in der sie im Template vorkommen */
function orderItemsByTemplate(items = [], template = '', regexp = null) {
	if (!regexp) {
		return items;
	}

	let itemOrder = template.match(regexp) || [];

	if (itemOrder) {
		itemOrder = itemOrder
			.filter((item, index, self) => self.indexOf(item) === index)
			.map(item => item.match(/"(.*?)"/)[1]);
	}

	const itemsNotInUse = Object.keys(items)
		.reduce((notInUseList, itemKey) => {
			const item = items[itemKey];
			if (itemOrder.indexOf(itemKey) === -1) {
				notInUseList.push(item);
			}
			return notInUseList;
		}, []);

	const itemsInUse = itemOrder.map(item =>
		items[item]);

	return itemsInUse.concat(itemsNotInUse);
}

const defaultSiteTranslations =
	Const.langs.reduce((acc, lang) => {
		acc[lang.key] = null;
		return acc;
	}, {});

const defaultSite = {
	id: null,
	current: {
		items: {},
		template: '',
		dirtyItems: [],
	},
	translations: defaultSiteTranslations,
	uncommited: null,
};

const objectSites = {
	meta: {
		ids: [],
		lang: '',
		fetched: false,
	},
	sites: [],
	sitesFinishedLoading: false,
	objectdata: {
		current: {
			items: [],
		},
	},
	layoutAction: {
		type: null,
		values: null,
	},
	templates: {},
	templateSets: [],
	sitesArePrepared: false,
};

export default {
	state: JSON.parse(JSON.stringify(objectSites)),

	mutations: {
		resetObjectSites(state) {
			state.meta = JSON.parse(JSON.stringify(objectSites.meta));
			state.sites = JSON.parse(JSON.stringify(objectSites.sites));
			state.sitesFinishedLoading = JSON.parse(JSON.stringify(objectSites.sitesFinishedLoading));
			state.objectdata = JSON.parse(JSON.stringify(objectSites.objectdata));
		},

		prepareObjectSites(state, meta) {
			meta.sites.forEach((site) => {
				const dummySite = JSON.parse(JSON.stringify(defaultSite));
				dummySite.id = site.PKM_Seite;
				dummySite.current = {
					meta: { ...site, loading: true },
					template: '',
				};
				dummySite.uncommited = new Set();
				dummySite.translations = JSON.parse(JSON.stringify(defaultSiteTranslations));

				if (site.S_Kurzbezeichnung === 'objektdaten') {
					dummySite.current = site;
					dummySite.loading = true;
					state.objectdata = dummySite;
				} else {
					const alreadyExistingSite =
						state.sites.some(currSite => currSite.id === dummySite.id);

					if (!alreadyExistingSite) {
						state.sites.push(dummySite);
					}
				}
			});

			state.sitesArePrepared = true;
		},

		setObjectSites(state, data) {
			data.siteObjects.forEach((siteObject) => {
				const site = JSON.parse(JSON.stringify(defaultSite));
				site.id = siteObject.id;
				site.current = siteObject;
				site.uncommited = new Set();
				site.translations = JSON.parse(JSON.stringify(defaultSiteTranslations));

				if (site.translations[data.lang] === null) {
					site.translations[data.lang] = siteObject;
				}

				Object.keys(site.current.items).forEach((itemKey) => {
					if (!site.current.items[itemKey].value) {
						site.current.items[itemKey].value = '';
					}
				});

				if (siteObject.meta.S_Kurzbezeichnung === 'objektdaten') {
					state.objectdata = site;
					state.objectdata.loading = false;
				} else {
					const foundExistingSite = state.sites.find(currSite => currSite.id === site.id);
					Vue.set(foundExistingSite, 'current', site.current);
					const mergedTranslations = JSON.parse(JSON.stringify(foundExistingSite.translations));

					Object.keys(site.translations).forEach((transLang) => {
						const trans = site.translations[transLang];

						if (trans) {
							mergedTranslations[transLang] = trans;
						}
					});

					Vue.set(foundExistingSite, 'translations', mergedTranslations);
				}
			});
		},
		setObjectSiteTranslation(state, data) {
			const site = state.sites.find(currSite => (currSite.id === data.siteObject.id));

			if (!site) {
				return;
			}

			if (site.translations[data.lang] === null) {
				Vue.set(site.translations, data.lang, data.siteObject);
			}
		},
		setObjectDataTranslation(state, data) {
			if (state.objectdata.translations[data.lang] === null) {
				Vue.set(state.objectdata.translations, data.lang, data.siteObject);
			}
		},
		setSitesMeta(state, meta) {
			state.meta.ids = meta.sites.map(site => site.PKM_Seite);
			state.meta.lang = meta.lang;
			state.meta.fetched = meta.fetched;
		},
		clearSitesUncommited(state, siteId) {
			let sites = state.sites;

			if (siteId) {
				sites = state.sites.filter(site => (site.id === siteId));
			}

			sites.forEach(site => site.uncommited.clear());
		},
		updateObjectDataItems(state, data) {
			Object.keys(data).forEach((itemKey) => {
				state.objectdata.current.items[itemKey].value = data[itemKey];
			});
		},
		updateObjectDataItemWithTranslations(state, data) {
			const activeLangKey = data.activeLangKey;
			Object.keys(data.sets).forEach((langKey) => {
				const items = data.sets[langKey];
				Object.keys(items).forEach((itemKey) => {
					state.objectdata.translations[langKey].items[itemKey].value = items[itemKey];

					if (activeLangKey === langKey) {
						state.objectdata.current.items[itemKey].value = items[itemKey];
					}
				});
			});
		},
		updateObjectSiteItems(state, data) {
			const siteId = data.siteId;
			const site = state.sites.find(currSite => (currSite.id === siteId));

			if (data.type === 'text' && data.items.sets) {
				const activeLangKey = data.activeLangKey;
				Object.keys(data.items.sets).forEach((langKey) => {
					const items = data.items.sets[langKey];
					Object.keys(items).forEach((itemKey) => {
						site.translations[langKey].items[itemKey].value = items[itemKey];

						if (activeLangKey === langKey) {
							site.current.items[itemKey].value = items[itemKey];
						}
					});
				});
			} else {
				Object.keys(data.items).forEach((key) => {
					switch (data.type) {
						case 'enum':
							site.current.items[key].value = data.items[key];
							break;

						case 'image':
							Vue.set(site.current.items, key,
							Object.assign({}, site.current.items[key], data.items[key]));

							/* TODO: Weitere Daten setzen */
							break;

						default: /* Nichts tun :( */
					}

					site.uncommited.add(key);
				});
			}
		},

		updateAssetsItem(state, data) {
			Object.keys(state.objectdata.current.items).forEach((fieldId) => {
				Object.keys(data).forEach((changedFieldId) => {
					if (changedFieldId === fieldId) {
						const fieldData = JSON.parse(JSON.stringify(state.objectdata.current.items[fieldId]));
						const updatedFieldData = { ...fieldData, ...data[changedFieldId] };
						Vue.set(state.objectdata.current.items, fieldId, updatedFieldData);
					}
				});
			});
		},

		setLayoutAction(state, action) {
			switch (action.type) {
				case 'copy':
				case 'move':
					state.layoutAction = action;
					break;

				default:
					/* TODO: Use (better) logging; 'Unbekannte, längerlebige Layout-Aktion:', action.type) */
			}
		},

		resetLayoutAction(state) {
			state.layoutAction = {
				type: null,
				values: null,
			};
		},

		moveSite(state, actionInfo) {
			const siteToBeMoved = state.sites.find(site => (site.current.id === actionInfo.values.id));
			const siteToBeMovedIndex = state.sites.indexOf(siteToBeMoved);
			const siteTopicId = actionInfo.values.topicId;
			state.sites.splice(siteToBeMovedIndex, 1);

			if (actionInfo.prevSiteId) {
				const prevSiteId = actionInfo.prevSiteId;
				const prevSiteItem = state.sites.find(site => (site.current.id === prevSiteId));
				const prevSiteItemIndex = state.sites.indexOf(prevSiteItem);

				state.sites.splice(prevSiteItemIndex + 1, 0, siteToBeMoved);
			} else if (actionInfo.nextSiteId) {
				const nextSiteId = actionInfo.nextSiteId;
				const nextSiteItem = state.sites.find(site => (site.current.id === nextSiteId));
				const nextSiteItemIndex = state.sites.indexOf(nextSiteItem);

				state.sites.splice(nextSiteItemIndex, 0, siteToBeMoved);
			} else {
				state.sites.splice(0, 0, siteToBeMoved);
			}

			let indexRunner = 0;
			state.sites.forEach((site) => {
				/* Nur die Seiten neu durchnummerieren, die zum gleichen Thema gehören */
				if (parseInt(site.current.meta.FKM_Thema, 10) !== siteTopicId) {
					return;
				}
				site.current.meta.U_Seitennummer = indexRunner;
				indexRunner += 1;
			});
		},

		setLayoutTemplatesLoadingStatus(state, templateInfo) {
			if (!state.templates[templateInfo.topicId]) {
				state.templates[templateInfo.topicId] = {
					items: {},
					loading: false,
				};
			}
			state.templates[templateInfo.topicId].loading = templateInfo.loading;
		},
		setLayoutTemplates(state, templateInfo) {
			state.templates[templateInfo.topicId].items = templateInfo.templatesData.templates;
		},

		insertLayoutSite(state, newSiteData) {
			state.sites.forEach((site) => {
				if (parseInt(site.current.meta.FKM_Thema, 10) !== newSiteData.topicId) {
					return;
				}

				const currSitenumber = parseInt(site.current.meta.U_Seitennummer, 10);

				if (currSitenumber >= newSiteData.newSitenumber) {
					site.current.meta.U_Seitennummer = currSitenumber + 1;
				}
			});

			Object.keys(newSiteData.siteData[0].items).forEach((itemKey) => {
				if (!newSiteData.siteData[0].items[itemKey].value) {
					newSiteData.siteData[0].items[itemKey].value = '';
				}
			});

			/* Da sich die eigentliche Reihenfolge durch die Seitennummer ergibt,
				kann man den neuen Eintrag hinten anfügen */
			const newSite = Object.assign({}, JSON.parse(JSON.stringify(defaultSite)), {
				current: newSiteData.siteData[0],
				id: newSiteData.siteData[0].id,
				uncommited: new Set(),
			});

			newSite.translations[newSiteData.lang] = newSiteData.siteData[0];

			state.sites.push(newSite);
		},

		deleteSite(state, id) {
			state.sites = state.sites.filter(site => site.id !== id);
		},

		setSitesFinishedLoading(state, status) {
			state.sitesFinishedLoading = status;
		},

		setTemplateSets(state, sets) {
			state.templateSets = sets;
		},

		resetSitesArePrepared(state) {
			state.sitesArePrepared = false;
		},
	},

	actions: {
		resetObjectSites({ commit }) {
			commit('resetObjectSites');
		},

		getObjectSites({ state, commit, dispatch }, meta) {
			return new Promise((resolve, reject) => {
				if (meta.sites.length === 1 &&
					meta.sites[0].S_Kurzbezeichnung === 'objektdaten') {
					/* Wenn nur Objektdaten vorliegen, dann sollen die Vorlagen-Sets angefragt werden,
				       um aus ihnen auszuwählen */
					dispatch('getTemplateSets');
				}

				if (meta.sites.length === 0) {
					reject('Action: getObjectSites => Keine Seiten!');
				}

				if (!state.sitesArePrepared || state.sites.length === 0) {
					commit('prepareObjectSites', meta);
				}

				const promisifiedRequests =	meta.sites.map(site =>
					new Promise((subResolve, subReject) => {
						ObjectSitesService.get([site], meta.lang)
						.then((siteObjects) => {
							commit('setObjectSites', { lang: meta.lang, siteObjects });
							subResolve();
						})
						.catch((response) => {
							subReject(response);
						});
					}),
				);

				Promise.all(promisifiedRequests)
				.then(() => {
					commit('setSitesFinishedLoading', true);
					commit('setSitesMeta', {
						sites: meta.sites,
						lang: meta.lang,
						fetched: true,
					});
					resolve();
				})
				.catch(() => {
					reject();
				});
			});
		},
		getObjectSiteTranslation({ commit }, meta) {
			return new Promise((resolve, reject) => {
				ObjectSitesService.get([meta.site], meta.lang)
				.then((siteObjects) => {
					commit('setObjectSiteTranslation', { lang: meta.lang, siteObject: siteObjects[0] });
					resolve(siteObjects[0]);
				})
				.catch((response) => {
					reject(response);
				});
			});
		},
		getObjectDataTranslation({ commit }, meta) {
			return new Promise((resolve, reject) => {
				ObjectSitesService.get([meta.site], meta.lang)
				.then((siteObjects) => {
					commit('setObjectDataTranslation', { lang: meta.lang, siteObject: siteObjects[0] });
					resolve(siteObjects[0]);
				})
				.catch((response) => {
					reject(response);
				});
			});
		},
		clearSites({ commit }, siteId) {
			commit('clearSitesUncommited', siteId);
		},
		saveSites({ state, commit }) {
			const data = {};
			state.sites.forEach((site) => {
				site.uncommited.forEach((key) => {
					if (!data[site.id]) {
						data[site.id] = {};
					}
					data[site.id][key] = {
						U_Position: site.current.items[key].U_Position,
						value: site.current.items[key].value,
					};
				});
			});

			ObjectSitesService.save(Object.keys(data), state.meta.lang, data)
			.then(() => {
				commit('clearSitesUncommited');
			});
		},

		handleSiteAction({ commit, dispatch, getters, rootState }, actionInfo) {
			const topicId = parseInt(rootState.objectsetup.current.thema_content, 10);

			return new Promise((resolve, reject) => {
				switch (actionInfo.type) {
					case 'move':
						commit('moveSite', actionInfo);

						ObjectSitesService.setSiteOrder(getters.mappedSiteData
						                                       .map(site => site.id)
						                                       .join(','),
							                            topicId)
						.then(() => {
							dispatch('resetLayoutAction');
							resolve();
						})
						.catch(() => {
							reject();
						});

						break;

					/* TODO: Unterstützung für weitere Seiten-/Modulaktionen */

					default:
						reject();
				}
			});
		},

		updateObjectDataItem({ commit, state, rootState }, values) {
			const siteId = values.siteId;
			delete values.siteId;
			delete values.type;

			if (values.sets) {
				const activeLangKey = rootState.objectsetup.current.sprache;
				commit('updateObjectDataItemWithTranslations', { activeLangKey, ...values });

				const promisifiedTranslationSiteRequests = [];

				Object.keys(values.sets).forEach((langKey) => {
					const dataObj = {};
					const langMappedItems = {};
					const changedItemKeys = Object.keys(values.sets[Object.keys(values.sets)[0]]);
					changedItemKeys.forEach((itemKey) => {
						const item = state.objectdata.translations[langKey].items[itemKey];
						langMappedItems[itemKey] = {
							...item,
							sprache: langKey,
						};
					});

					dataObj[siteId] = langMappedItems;

					promisifiedTranslationSiteRequests.push(
						ObjectSitesService.save([siteId], state.meta.lang, dataObj),
					);
				});

				return Promise.all(promisifiedTranslationSiteRequests);
			}

			commit('updateObjectDataItems', values);
			const dataObj = {};
			dataObj[siteId] = {};

			Object.keys(values).forEach((key) => {
				const item = state.objectdata.current.items[key];
				dataObj[siteId][key] = {
					...item,
					sprache: state.meta.lang,
				};
			});
			return ObjectSitesService.save([siteId], state.meta.lang, dataObj);
		},

		updateObjectSiteItems({ commit, state, rootState }, values) {
			const siteId = values.siteId;
			delete values.siteId;
			const type = values.type;
			delete values.type;

			if (!Object.keys(values).length) {
				return Promise.reject(`Kein(e) Wert(e) gesetzt: ${type}`);
			}

			const activeLangKey = rootState.objectsetup.current.sprache;
			commit('updateObjectSiteItems', { siteId, type, activeLangKey, items: values });

			/* Besondere Behandlung von Textfeldern,
				weil auch Übersetzungen berücksichtigt werden müssen */
			if (type === 'text' && values.sets) {
				const site = state.sites.find(currSite => (currSite.id === siteId));

				const promisifiedTranslationSiteRequests = [];

				Object.keys(values.sets).forEach((langKey) => {
					const dataObj = {};
					const langMappedItems = {};
					const changedItemKeys = Object.keys(values.sets[Object.keys(values.sets)[0]]);
					changedItemKeys.forEach((itemKey) => {
						const item = site.translations[langKey].items[itemKey];
						langMappedItems[itemKey] = {
							...item,
							sprache: langKey,
						};
					});

					dataObj[siteId] = langMappedItems;

					promisifiedTranslationSiteRequests.push(
						ObjectSitesService.save([siteId], state.meta.lang, dataObj),
					);
				});

				return Promise.all(promisifiedTranslationSiteRequests);
			} else if (type === 'enum') {
				const site = state.sites.find(currSite => (currSite.id === siteId));
				const itemKey = Object.keys(values)[0];
				const item = site.current.items[itemKey];

				const dataObj = {};

				dataObj[siteId] = {};
				dataObj[siteId][itemKey] = {
					U_Position: item.U_Position,
					value: values[itemKey],
					sprache: state.meta.lang,
				};

				return ObjectSitesService.save([siteId], state.meta.lang, dataObj);
			} else if (type === 'image') {
				const promisifiedRequests = [];
				const dataObj = {};
				dataObj[siteId] = values;

				promisifiedRequests.push(
					ObjectSitesService.save([siteId], state.meta.lang, dataObj),
				);

				return Promise.all(promisifiedRequests);
			}

			return Promise.reject(`Unbekannter Typ: ${type}`);
		},

		updateObjectSiteItemsAndSave({ dispatch, commit }, values) {
			dispatch('updateObjectSiteItems', values)
			.then(() =>
				new Promise((resolve) => {
					commit('clearSitesUncommited');
					resolve();
				}),
			)
			.catch(() => { /* TODO: Use (better) logging */ });
		},

		updateAssetsItem({ commit, state, rootState }, values) {
			commit('updateAssetsItem', values);

			const siteId = state.objectdata.id;

			const promisifiedRequests = [];
			const dataObj = {};
			dataObj[siteId] = values;

			promisifiedRequests.push(
				ObjectSitesService.save([siteId], state.meta.lang, dataObj),
			);

			return Promise.all(promisifiedRequests);
		},

		deleteImageById({ state, rootState }, imageId) {
			const objId = rootState.objectsetup.current.PKM_Objekt;
			return ObjectSitesService.deleteImage(imageId, state.meta.lang, objId);
		},

		updateAssetsItemFromMAM({ commit, dispatch, state, rootState }, data) {
			const currentSelectedLang = rootState.objectsetup.language;

			return new Promise((resolve, reject) => {
				ObjectSitesService.saveMAMImage(data, currentSelectedLang)
				.then((response) => {
					if (response.success === 'true') {
						if (state.objectdata.id !== data.PKM_Seite) {
							const commitData = {};
							commitData.siteId = data.PKM_Seite;
							commitData.type = 'image';
							commitData.items = {};
							commitData.items[`bildfeld_${data.U_Position}`] = {
								value: `${process.env.IMG_SERVER}${response.url}`,
							};
							commit('updateObjectSiteItems', commitData);
						} else {
							const commitData = {};
							commitData[`bildfeld_${data.U_Position}`] = {
								value: `${process.env.IMG_SERVER}${response.url}`,
							};
							commit('updateAssetsItem', commitData);
						}

						dispatch('updateImageInHistory', { key: `bildfeld_${data.U_Position}`, value: `${process.env.IMG_SERVER}${response.url}` });
						resolve();
					} else {
						reject();
					}
				})
				.catch((err) => {
					reject(err);
				});
			});
		},

		setLayoutAction({ commit }, action) {
			commit('setLayoutAction', action);
		},

		resetLayoutAction({ commit }) {
			commit('resetLayoutAction');
		},

		getLayoutTemplatesForTopicId({ commit, rootState }, topicId) {
			const objectId = parseInt(rootState.objectsetup.current.PKM_Objekt, 10);

			commit('setLayoutTemplatesLoadingStatus', { topicId, loading: true });

			return new Promise((resolve, reject) => {
				ObjectSitesService.getTemplates(objectId, topicId)
				.then((templatesData) => {
					commit('setLayoutTemplates', { topicId, templatesData });
					commit('setLayoutTemplatesLoadingStatus', { topicId, loading: false });
					resolve();
				})
				.catch((e) => {
					commit('setLayoutTemplatesLoadingStatus', { topicId, loading: false });
					reject(e);
				});
			});
		},

		getLayoutTemplates({ dispatch, getters }) {
			getters.topicIds.forEach(topicId =>
				dispatch('getLayoutTemplatesForTopicId', topicId),
			);
		},

		insertLayoutSite({ commit, state, rootState }, data) {
			const objectId = parseInt(rootState.objectsetup.current.PKM_Objekt, 10);
			const selectedTemplateId = data.selectedTemplateId;
			const topicId = data.topicId;
			const newSitenumber = data.sitenumber;
			const lang = state.meta.lang;

			return new Promise((resolve, reject) => {
				ObjectSitesService.insertSiteAtPosition(objectId,
				                                        selectedTemplateId,
				                                        topicId,
				                                        newSitenumber)
				.then((responseData) => {
					if (!responseData.neue_FKM_Seite) {
						reject({
							error: 'Neue FKM_Seite ist null!',
						});
					}

					const newSite = {
						...data.selectedTemplateData,
						FKM_Template: data.selectedTemplateData.PKM_Template,
						FKM_Thema: topicId,
						PKM_Seite: responseData.neue_FKM_Seite,
						U_Seitennummer: newSitenumber,
					};

					/* Information über neu erstellte Seite / Modul holen */
					ObjectSitesService.get([newSite], lang)
					.then((siteData) => {
						commit('insertLayoutSite', { siteData, newSitenumber, topicId, lang });
						resolve();
					})
					.catch((e) => {
						reject(e);
					});
				})
				.catch((e) => {
					reject(e);
				});
			});
		},

		deleteSite({ commit, state, dispatch, rootState }, id) {
			const objectId = parseInt(rootState.objectsetup.current.PKM_Objekt, 10);
			return new Promise((resolve, reject) => {
				ObjectSitesService.delete(id, objectId)
				.then(() => {
					commit('deleteSite', id);

					/* Wenn alle Seiten gelöscht wurden,
					   sollten die Template-Sets angefragt und wieder zur Auswahl stehen */
					if (state.sites.length === 0) {
						dispatch('getLayoutTemplates');
					}
					resolve();
				})
				.catch(() => {
					reject();
				});
			});
		},

		getTemplateSets({ commit, rootState }) {
			const objectId = parseInt(rootState.objectsetup.current.PKM_Objekt, 10);

			return new Promise((resolve, reject) => {
				ObjectSitesService.getTemplateSets(objectId)
				.then((response) => {
					commit('setTemplateSets', response.sets);
					resolve(response);
				})
				.catch((response) => {
					reject(response);
				});
			});
		},

		createTemplateSet({ commit, rootState }, objectTemplateSetId) {
			const objectId = parseInt(rootState.objectsetup.current.PKM_Objekt, 10);

			return new Promise((resolve, reject) => {
				ObjectSitesService.createTemplateSet(objectId, objectTemplateSetId)
				.then((response) => {
					resolve(response);
				})
				.catch((response) => {
					reject(response);
				});
			});
		},

		resetSitesArePrepared({ commit }) {
			commit('resetSitesArePrepared');
		},

	},

	getters: {
		mappedSiteData(state, getters, rootState) {
			const sites = {};

			const lang = (rootState.objectsetup.current &&
			                rootState.objectsetup.current.sprache) || rootState.objectsetup.language;

			state.sites.forEach((site) => {
				const templateId = parseInt(site.current.meta.FKM_Template, 10);
				const template = site.current.template.replace(/ src="(.+?)"/, (match, path) => {
					const resolvedUrl = url.resolve(process.env.IMG_SERVER, path);
					return ` src="${resolvedUrl}"`;
				});

				const topicId = parseInt(site.current.meta.FKM_Thema, 10) || 0;

				const templateTitle = (state.templates[topicId] &&
				                       state.templates[topicId].items &&
				                       state.templates[topicId].items[templateId] &&
				                       state.templates[topicId].items[templateId].S_Titel) || '';

				sites[site.id] = {
					id: site.id,
					key: site.id,
					topicId,
					topicOrderNum: getters.themeIdToOrder[topicId] || 0,
					sitenumber: parseInt(site.current.meta.U_Seitennummer, 10),
					template,
					templateId,
					templateTitle,
					templatePath: site.current.meta.S_Templatepfad,
					loading: site.current.meta.loading,
					data: {},
					lang,
					translations: site.translations,
					items: Vue.util.extend({}, site.current.items),
					bodytagAttrs: site.current.bodytag_attributes || '',
				};

				Object.keys(site.current.items || []).forEach((key) => {
					if (site.current.items[key].enum) {
						sites[site.id].data[key] = site.current.items[key].enum.reduce((value, item) =>
							(item.value.trim() === site.current.items[key].value.trim() ? item.label : value)
						, '');
					} else if (site.current.items[key].typ === 'text') {
						sites[site.id].data[key] = TextFormat.format(site.current.items[key].value);
					} else {
						sites[site.id].data[key] = site.current.items[key].value;
					}
				});
			});


			const sitesArray = Object.keys(sites).map(siteId => sites[siteId]);
			const sortedSites = sitesArray.sort((a, b) => {
				/* Zuerst nach Thema sortieren */
				const sortRes = a.topicOrderNum - b.topicOrderNum;

				/* Bei gleichem Thema, in der nächsten Stufe nach Seitennummer sortieren */
				if (sortRes === 0) {
					return (a.sitenumber - b.sitenumber);
				}

				return sortRes;
			});

			return sortedSites;
		},

		mappedObjectDataItems(state) {
			const result = {
				template: '',
				textItems: {},
				imageItems: {},
				seoItems: {},
				textItemsTranslations: state.objectdata.translations,
			};

			if (state.objectdata.current.template) {
				result.template = state.objectdata.current.template;
			}

			Object.keys(state.objectdata.current.items || []).forEach((itemKey) => {
				const item = state.objectdata.current.items[itemKey];
				const labelKey = (item.label || '').trim().toLowerCase()
									.replace(/[:$&?!.,()/]*/mg, '')
									.replace(/([\s-][a-z])/mg, match => match[1].toUpperCase())
									.replace(/[\s-]/, '');
				const mappedItem = {
					key: itemKey,
					labelKey,
					...item,
					charlength: (item.charlength && parseInt(item.charlength, 10)) || false,
				};

				if (item.typ === 'bild') {
					result.imageItems[itemKey] = mappedItem;
				} else {
					result.textItems[itemKey] = mappedItem;
				}
			});

			result.imageItems = orderItemsByTemplate(result.imageItems, result.template, /v-bind:src="(.*?)"/mg);

			const orderedTextItems = orderItemsByTemplate(result.textItems, result.template, /v-html="(.*?)"/mg);
			const {
				orderedObjectDataTextItems,
				orderedSeoTextItems,
			} = orderedTextItems.reduce((acc, item) => {
				if (item.cardtab === 'seo') {
					acc.orderedSeoTextItems.push(item);
				} else {
					acc.orderedObjectDataTextItems.push(item);
				}

				return acc;
			}, { orderedObjectDataTextItems: [], orderedSeoTextItems: [] });

			result.textItems = orderedObjectDataTextItems;
			result.seoItems = orderedSeoTextItems;

			return result;
		},

		maxSitesPerTopicStatus(state, getters, rootState) {
			const sitesPerTopicStatus = {};

			state.sites.forEach((site) => {
				const topicId = parseInt(site.current.meta.FKM_Thema, 10);
				const topic = (rootState.objectsetup.current.themen || [])
				                       .find(currTopic => parseInt(currTopic.PKM_Thema, 10) === topicId);

				if (!sitesPerTopicStatus[topicId]) {
					sitesPerTopicStatus[topicId] = {
						cnt: 0,
						max: (topic && parseInt(topic.U_Max_Seitenzahl, 10)) || 0,
					};
				}

				sitesPerTopicStatus[topicId].cnt += 1;
			});

			Object.keys(sitesPerTopicStatus).forEach((topicId) => {
				sitesPerTopicStatus[topicId] =
					sitesPerTopicStatus[topicId].cnt >= sitesPerTopicStatus[topicId].max;
			});

			return sitesPerTopicStatus;
		},

		isMandatoryFilledOut(state) {
			if (!state.objectdata.current.items) return false;
			return Object.keys(state.objectdata.current.items).every((key) => {
				const isMandatory = Object.prototype.hasOwnProperty.call(state.objectdata.current.items[key], 'mandatory')
					&& state.objectdata.current.items[key].mandatory === 'true';

				if (!isMandatory) {
					return true;
				}
				if (!state.objectdata.current.items[key].value) {
					return false;
				}
				return true;
			});
		},
	},
};



// WEBPACK FOOTER //
// ./src/store/modules/objectsites.js